import React, { useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { slideInUp } from '../../utils/motion.config';
import UserProgressContext from '../../userProgressContext';

let ProgressMap = null;
try {
  ProgressMap = require('../UI/ProgressMap').default;
} catch (_) {}

const TITLE = 'LEVEL COMPLETE';

/**
 * LevelSummary — End-of-level concept recap and Defense Score delta.
 *
 * Props:
 *   step       — { summary: { concepts: string[] } }
 *   scoreDelta — number — points earned this level
 *   onComplete — () => void — called when player dismisses summary
 */
function LevelSummary({ step, scoreDelta = 0, onComplete }) {
  const { totalScore, localProgress } = useContext(UserProgressContext);
  const [startScore] = React.useState(() => parseInt(totalScore) || 0);

  const computedDelta =
    scoreDelta > 0
      ? scoreDelta
      : Math.max(0, (parseInt(totalScore) || 0) - startScore);

  const concepts =
    step && Array.isArray(step.summary?.concepts) ? step.summary.concepts : [];

  const completedLevel = Math.min(6, Math.max(1, Math.trunc(parseFloat(localProgress) || 1)));
  const characterNode = completedLevel + 1;

  return (
    <AnimatePresence>
      <motion.div
        className="flex flex-col gap-6 w-full h-full text-white rounded-xl p-6 md:p-10 overflow-y-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {/* ProgressMap */}
        {ProgressMap && (
          <div className="rounded-lg overflow-hidden border border-indigo-700/50">
            <ProgressMap characterNode={characterNode} fromNode={completedLevel} />
          </div>
        )}

        {/* ── Hero title with scanline sweep ── */}
        <div className="relative text-center overflow-hidden rounded-xl py-8 px-4"
          style={{ background: 'linear-gradient(135deg, #0f172a 60%, #1e1b4b 100%)' }}
        >
          {/* Scanline sweep */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(180deg, transparent 0%, rgba(99,102,241,0.18) 50%, transparent 100%)',
              transformOrigin: 'top',
            }}
            initial={{ scaleY: 0, y: '-100%' }}
            animate={{ scaleY: 1, y: '100%' }}
            transition={{ duration: 0.55, ease: 'easeInOut' }}
          />

          {/* Horizontal accent line */}
          <motion.div
            className="absolute left-0 right-0 h-px bg-indigo-400/60"
            style={{ top: '50%' }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          />

          {/* Letter-stagger title */}
          <div className="relative flex justify-center flex-wrap gap-x-[0.05em] gap-y-0">
            {TITLE.split('').map((char, i) => (
              <motion.span
                key={i}
                className={
                  char === ' '
                    ? 'inline-block w-4'
                    : 'inline-block text-3xl md:text-4xl font-black tracking-[0.12em] text-white'
                }
                style={
                  char !== ' '
                    ? { textShadow: '0 0 18px rgba(129,140,248,0.8), 0 0 40px rgba(99,102,241,0.4)' }
                    : undefined
                }
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: 0.2 + i * 0.04, ease: 'easeOut' }}
              >
                {char}
              </motion.span>
            ))}
          </div>

          {/* Subtitle tag */}
          <motion.p
            className="relative mt-3 text-[11px] tracking-[0.3em] uppercase text-indigo-400 font-semibold"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            Mission Accomplished
          </motion.p>

          {/* Corner accents */}
          {[['top-2 left-2', 'border-t border-l'], ['top-2 right-2', 'border-t border-r'],
            ['bottom-2 left-2', 'border-b border-l'], ['bottom-2 right-2', 'border-b border-r']].map(([pos, border], i) => (
            <motion.span
              key={i}
              className={`absolute w-3 h-3 ${pos} ${border} border-indigo-500/70`}
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 + i * 0.06 }}
            />
          ))}
        </div>

        {/* Defense Score delta */}
        {computedDelta > 0 && (
          <motion.div
            className="text-center text-2xl font-semibold text-green-400"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35, delay: 0.7 }}
          >
            +{computedDelta} pts
            <span className="block text-sm text-green-300 font-normal mt-1">
              Defense Score earned
            </span>
          </motion.div>
        )}

        {/* Key concept cards */}
        {concepts.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0 }}
          >
            <h3 className="text-sm uppercase tracking-widest text-indigo-300 mb-3 font-semibold">
              Key Concepts
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {concepts.map((concept, index) => (
                <motion.div
                  key={index}
                  className="bg-indigo-900/60 rounded-lg p-4 text-sm text-indigo-100 leading-relaxed"
                  initial={slideInUp.initial}
                  animate={slideInUp.animate}
                  transition={{ duration: 0.35, delay: 1.1 + index * 0.1 }}
                >
                  {concept}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Dismiss */}
        <motion.div
          className="flex justify-center mt-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0 }}
        >
          <button
            onClick={onComplete}
            className="px-8 py-3 bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white font-semibold rounded-lg transition-colors duration-150 text-lg"
          >
            Continue →
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default LevelSummary;
